import { useState, ChangeEvent, FormEvent } from 'react';
import type { SSIFormData, SSIAnalysisState, SSIFormErrors, SSIPillarInput } from '../types';

const PILLAR_LABELS = [
  'Pilar 1 — Estabeleça sua marca profissional',
  'Pilar 2 — Encontre as pessoas certas',
  'Pilar 3 — Interaja com insights',
  'Pilar 4 — Construa relacionamentos',
];

const initialPilares: SSIPillarInput[] = [1, 2, 3, 4].map((n) => ({ number: n, score: '' }));

interface SSIFormProps {
  onSubmit: (data: SSIFormData) => void;
  errors: SSIFormErrors;
  status: SSIAnalysisState;
}

export default function SSIForm({ onSubmit, errors, status }: SSIFormProps) {
  const [formData, setFormData] = useState<SSIFormData>({
    cargo_atual: '',
    area_interesse: '',
    texto_sobre: '',
    pilares: initialPilares,
  });

  const isLoading = status === 'loading';

  function handleTextChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  function handlePillarChange(index: number, value: string) {
    setFormData((prev) => {
      const pilares = [...prev.pilares];
      pilares[index] = { ...pilares[index], score: value === '' ? '' : Number(value) };
      return { ...prev, pilares };
    });
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    onSubmit(formData);
  }

  return (
    <form onSubmit={handleSubmit} noValidate aria-label="Formulário de análise SSI">
      <div className="space-y-6">
        <div>
          <label htmlFor="cargo_atual" className="block font-inter font-medium text-zinc-700 mb-1">
            Cargo atual <span aria-hidden="true" className="text-red-500">*</span>
          </label>
          <input
            id="cargo_atual"
            name="cargo_atual"
            type="text"
            value={formData.cargo_atual}
            onChange={handleTextChange}
            required
            maxLength={200}
            placeholder="Ex.: Gerente de Marketing"
            className="w-full rounded-lg border border-zinc-300 px-4 py-3 font-inter text-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            aria-describedby={errors.cargo_atual ? 'error-cargo_atual' : undefined}
            aria-invalid={!!errors.cargo_atual}
          />
          {errors.cargo_atual && (
            <p id="error-cargo_atual" role="alert" className="mt-1 text-sm text-red-600 font-inter">
              {errors.cargo_atual}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="area_interesse" className="block font-inter font-medium text-zinc-700 mb-1">
            Áreas de interesse <span aria-hidden="true" className="text-red-500">*</span>
          </label>
          <input
            id="area_interesse"
            name="area_interesse"
            type="text"
            value={formData.area_interesse}
            onChange={handleTextChange}
            required
            maxLength={500}
            placeholder="Ex.: B2B, SaaS, Growth"
            className="w-full rounded-lg border border-zinc-300 px-4 py-3 font-inter text-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            aria-describedby={errors.area_interesse ? 'error-area_interesse' : undefined}
            aria-invalid={!!errors.area_interesse}
          />
          {errors.area_interesse && (
            <p id="error-area_interesse" role="alert" className="mt-1 text-sm text-red-600 font-inter">
              {errors.area_interesse}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="texto_sobre" className="block font-inter font-medium text-zinc-700 mb-1">
            Texto da seção "Sobre" do perfil <span aria-hidden="true" className="text-red-500">*</span>
          </label>
          <textarea
            id="texto_sobre"
            name="texto_sobre"
            value={formData.texto_sobre}
            onChange={handleTextChange}
            required
            maxLength={2000}
            rows={6}
            placeholder="Cole aqui o texto da seção Sobre do seu perfil LinkedIn..."
            className="w-full rounded-lg border border-zinc-300 px-4 py-3 font-inter text-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-vertical"
            aria-describedby={errors.texto_sobre ? 'error-texto_sobre' : undefined}
            aria-invalid={!!errors.texto_sobre}
          />
          {errors.texto_sobre && (
            <p id="error-texto_sobre" role="alert" className="mt-1 text-sm text-red-600 font-inter">
              {errors.texto_sobre}
            </p>
          )}
        </div>

        <fieldset>
          <legend className="font-inter font-medium text-zinc-700 mb-3">
            Scores dos pilares SSI <span className="text-zinc-400 font-normal text-sm">(0 a 25 cada)</span>
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {PILLAR_LABELS.map((label, i) => {
              const pillarError = errors.pilares?.[i];
              const inputId = `pilar-${i + 1}`;
              const errorId = `error-pilar-${i + 1}`;
              return (
                <div key={i}>
                  <label htmlFor={inputId} className="block font-inter text-sm text-zinc-600 mb-1">
                    {label}
                  </label>
                  <input
                    id={inputId}
                    type="number"
                    min={0}
                    max={25}
                    step="any"
                    value={formData.pilares[i].score}
                    onChange={(e) => handlePillarChange(i, e.target.value)}
                    placeholder="0–25"
                    className="w-full rounded-lg border border-zinc-300 px-4 py-3 font-inter text-zinc-800 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    aria-describedby={pillarError ? errorId : undefined}
                    aria-invalid={!!pillarError}
                  />
                  {pillarError && (
                    <p id={errorId} role="alert" className="mt-1 text-sm text-red-600 font-inter">
                      {pillarError}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </fieldset>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full sm:w-auto px-8 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-400 text-white font-inter font-semibold rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
        >
          {isLoading ? 'Analisando…' : 'Analisar'}
        </button>
      </div>
    </form>
  );
}
